/**
 * Study group list content - manage reusable study groups.
 */

import { useState, useCallback } from 'react';
import { observer } from 'mobx-react-lite';
import { bibleState } from '@/state/bible-state';
import { PlusIcon, TrashIcon, ChevronRightIcon } from '@components/icons';
import type { BibleLocation } from '@skjv/common';
import { LocationDropTarget } from '../study-editor/location-drop-target';
import { GroupMembers } from './group-members';

export const StudyGroupList = observer(function StudyGroupList() {
  const groups = bibleState.sortedStudyGroups;
  // Accordion state lives in the store so the tour can open a new group
  const expandedGroupId = bibleState.expandedGroupId;
  const [newGroupName, setNewGroupName] = useState('');
  const [isAddingGroup, setIsAddingGroup] = useState(false);

  const handleCreateGroup = () => {
    if (newGroupName.trim()) {
      bibleState.addStudyGroup(newGroupName.trim());
      setNewGroupName('');
      setIsAddingGroup(false);
    }
  };

  return (
    <div className="flex flex-col gap-3">
      {/* Create new group button/form */}
      {isAddingGroup ? (
        <div className="rounded-xl border border-stone-300 bg-stone-50/50 p-3 dark:border-stone-600 dark:bg-stone-800/50">
          <input
            type="text"
            value={newGroupName}
            onChange={(e) => setNewGroupName(e.target.value)}
            placeholder="Group name..."
            className="w-full rounded-lg border border-stone-300 bg-white px-3 py-2 text-stone-800 placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-cyan-400 dark:border-stone-600 dark:bg-stone-800 dark:text-stone-200"
            autoFocus
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleCreateGroup();
              if (e.key === 'Escape') {
                setIsAddingGroup(false);
                setNewGroupName('');
              }
            }}
          />
          <div className="mt-2 flex gap-2">
            <button
              onClick={handleCreateGroup}
              disabled={!newGroupName.trim()}
              className="rounded-lg bg-cyan-500 px-3 py-1.5 text-sm text-white transition-colors hover:bg-cyan-600 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Create
            </button>
            <button
              onClick={() => {
                setIsAddingGroup(false);
                setNewGroupName('');
              }}
              className="px-3 py-1.5 text-sm text-stone-600 transition-colors hover:text-stone-800 dark:text-stone-400 dark:hover:text-stone-200"
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <button
          onClick={() => setIsAddingGroup(true)}
          data-guide="add-group"
          className="flex items-center gap-2 rounded-xl border-2 border-dashed border-stone-300 p-3 text-stone-500 transition-all hover:border-cyan-400 hover:text-cyan-600 dark:border-stone-600 dark:text-stone-400 dark:hover:border-cyan-500 dark:hover:text-cyan-400"
        >
          <PlusIcon className="h-5 w-5" />
          <span>Create new group</span>
        </button>
      )}

      {/* Groups list */}
      {groups.length === 0 ? (
        <div className="py-8 text-center text-stone-500 dark:text-stone-400">
          <p>No study groups yet.</p>
          <p className="mt-2 text-sm">Create a group to manage your students.</p>
        </div>
      ) : (
        groups.map((group) => (
          <div key={group.id} className="study-row">
            <div
              className="flex cursor-pointer items-center justify-between"
              onClick={() => bibleState.toggleGroupExpansion(group.id)}
            >
              <div className="flex items-center gap-2">
                <ChevronRightIcon
                  className={`h-4 w-4 transition-transform ${expandedGroupId === group.id ? 'rotate-90' : ''}`}
                />
                <div>
                  <div className="font-medium text-stone-800 dark:text-stone-200">{group.name}</div>
                  <div className="text-sm text-stone-500 dark:text-stone-400">
                    {group.students.length} student{group.students.length !== 1 ? 's' : ''} &bull;{' '}
                    {(group.teachers || []).length} teacher
                    {(group.teachers || []).length !== 1 ? 's' : ''}
                  </div>
                </div>
              </div>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  bibleState.showConfirmDialog({
                    title: 'Delete Group',
                    message: `Delete group "${group.name}"?`,
                    confirmText: 'Delete',
                    variant: 'danger',
                    onConfirm: () => bibleState.removeStudyGroup(group.id),
                  });
                }}
                className="p-1.5 text-stone-400 transition-colors hover:text-red-500"
                title="Delete group"
              >
                <TrashIcon className="h-4 w-4" />
              </button>
            </div>

            {expandedGroupId === group.id && (
              <>
                <GroupMembers groupId={group.id} />
                <MemoryPassageEditor groupId={group.id} />
              </>
            )}
          </div>
        ))
      )}
    </div>
  );
});

/**
 * Memory Passage editor for a study group.
 */
const MemoryPassageEditor = observer(function MemoryPassageEditor({
  groupId,
}: {
  groupId: string;
}) {
  const group = bibleState.getStudyGroup(groupId);

  const handleStartDrop = useCallback(
    (location: BibleLocation | null) => {
      bibleState.setStudyGroupMemoryStart(groupId, location);
    },
    [groupId],
  );

  const handleEndDrop = useCallback(
    (location: BibleLocation | null) => {
      bibleState.setStudyGroupMemoryEnd(groupId, location);
    },
    [groupId],
  );

  if (!group) return null;

  return (
    <div className="mt-4 border-t border-stone-200 pt-3 dark:border-stone-700">
      <h4 className="mb-2 text-sm font-medium text-stone-700 dark:text-stone-300">
        Memory Passage
      </h4>
      <p className="mb-3 text-xs text-stone-500 dark:text-stone-400">
        Drag verses here. Start clears end. End only extends.
      </p>
      <div className="grid grid-cols-2 gap-2">
        <LocationDropTarget
          label="Start"
          location={group.memoryStartingLocation}
          onDrop={handleStartDrop}
          placeholder="(none)"
        />
        <LocationDropTarget
          label="End"
          location={group.memoryEndingLocation}
          onDrop={handleEndDrop}
          placeholder="(same)"
        />
      </div>
    </div>
  );
});
